var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { Fragment, useState } from 'react';
import { useQuery } from '@apollo/client';
import { t } from '@lingui/core/macro';
import { Trans } from '@lingui/react/macro';
import { Button, Menu, useToast } from '@pocketlaw/tetris';
import { Falcon } from '@pocketlaw/tetris/macro';
import { useHistory } from 'react-router-dom';
import { DuplicateDocumentDialog, RenameDocumentDialog } from 'app/domains/documents';
import { DeleteDocumentDialog, ROOT, useACLCan, useDownloadFilesystemDocument } from 'app/domains/filesystem';
import { DocumentType } from 'shared/domains/apollo/generated/types';
import { documentFlush, usePreview } from 'shared/domains/documents';
import { SupportedFileMimeTypes } from 'shared/domains/filesystem';
import { DocumentActionsDropdownDocument } from './DocumentActionsDropdown.gql';
import { DownloadDocxItem } from './DownloadDocxItem';
import { DownloadPdfItem } from './DownloadPdfItem';
import { DownloadPleditorDocxItem } from './DownloadPleditorDocxItem';
import { ImportDocumentMenuItem } from './ImportDocumentMenuItem';
export function DocumentActionsDropdown(props) {
    const { documentId } = props;
    const { files } = useDownloadFilesystemDocument(documentId);
    const { isPleditorImportExportAvailable } = usePreview();
    const { addToast } = useToast();
    const history = useHistory();
    const [renderDeleteDialog, setShowDeleteDialog] = useState(false);
    const [renderRenameDialog, setShowRenameDialog] = useState(false);
    const [renderDuplicateDialog, setShowDuplicateDialog] = useState(false);
    const pdfFile = files.find((file) => file.type === SupportedFileMimeTypes.PDF);
    const docxFile = files.find((file) => file.type === SupportedFileMimeTypes.DOCX);
    const { data, loading, error: queryError, } = useQuery(DocumentActionsDropdownDocument, {
        variables: { id: documentId },
    });
    const { id = '', name = '', folderId, type, acls, teamAcls } = (data === null || data === void 0 ? void 0 : data.fileSystemDocument) || {};
    const { accessLevel } = useACLCan({ userAcls: acls, teamAcls });
    const restricted = accessLevel !== 'full' || loading || Boolean(queryError);
    const isRtcAvailable = type === DocumentType.Composed || type === DocumentType.Freetext;
    const showDeleteDialog = () => {
        setShowDeleteDialog(true);
    };
    const hideDeleteDialog = () => setShowDeleteDialog(false);
    const showRenameDialog = () => {
        setShowRenameDialog(true);
    };
    const hideRenameDialog = () => setShowRenameDialog(false);
    const showDuplicateDialog = () => {
        setShowDuplicateDialog(true);
    };
    const hideDuplicateDialog = () => setShowDuplicateDialog(false);
    const onDeleted = () => {
        if (isRtcAvailable) {
            documentFlush({ state: 'initialized' });
        }
        if (folderId && folderId !== ROOT) {
            return history.push(`/files/${folderId}`);
        }
        return history.push(`/files`);
    };
    const onDuplicated = (draftId) => {
        history.push(`/draft/${draftId}`);
    };
    const handleDuplicate = () => __awaiter(this, void 0, void 0, function* () {
        try {
            if (!id) {
                throw Error();
            }
            showDuplicateDialog();
        }
        catch (error) {
            addToast({ title: t `Could not create copy.`, appearance: 'danger' });
        }
    });
    const handleDownload = () => {
        addToast({
            appearance: 'info',
            title: t({
                comment: 'Toast message when the document download has been started',
                message: `Downloading ${name}`,
            }),
        });
    };
    return (<Fragment>
      <Menu.Root target={<Button disabled={loading} aria-label={t `More options`} appearance="secondary" size="xs">
            <Falcon icon="ellipsis-v"/>
            <Trans comment="Label for document actions dropdown">Actions</Trans>
          </Button>}>
        <Menu.Item onClick={showRenameDialog} disabled={restricted}>
          <Falcon icon="pen-to-square"/>
          <Menu.ItemText primary={t({ message: 'Edit name', comment: 'Dropdown label for renaming document' })}/>
        </Menu.Item>

        {/** Always allow downloading PDF file if its available */}
        {pdfFile && <DownloadPdfItem disabled={restricted} download={pdfFile.fetch} onDownload={handleDownload}/>}

        {/** If we have a DOCX file and the file is not composed or freetext, allow downloading the DOCX */}
        {docxFile && !isPleditorImportExportAvailable && (<DownloadDocxItem download={docxFile.fetch} onDownload={handleDownload}/>)}

        {/** If the file is composed or freetext we only allow downloading if customer has IMPORT_EXPORT feature */}
        {isPleditorImportExportAvailable && (<DownloadPleditorDocxItem disabled={restricted} onDownload={handleDownload}/>)}

        <ImportDocumentMenuItem disabled={restricted}/>

        <Menu.Item onClick={handleDuplicate} disabled={restricted}>
          <Falcon icon="copy"/>
          <Menu.ItemText primary={t({ message: 'Copy', comment: 'Dropdown label for copying document' })}/>
        </Menu.Item>

        <Menu.Divider />

        <Menu.Item onClick={showDeleteDialog} disabled={restricted}>
          <Falcon icon="trash-can"/>
          <Menu.ItemText primary={t({ message: 'Delete', comment: 'Dropdown label for deleting document' })}/>
        </Menu.Item>
      </Menu.Root>

      {renderDeleteDialog && id && <DeleteDocumentDialog id={id} onDeleted={onDeleted} onClose={hideDeleteDialog}/>}

      {renderRenameDialog && <RenameDocumentDialog onClose={hideRenameDialog}/>}

      {renderDuplicateDialog && id && (<DuplicateDocumentDialog id={id} onDuplicated={onDuplicated} onClose={hideDuplicateDialog}/>)}
    </Fragment>);
}
